import { getAccountTypeKey } from '@/sessionStorageConstants';

interface AccountTypeHolder {
  accountType: string;
}

interface ResolveAccountTypeArgs {
  accounts: Array<AccountTypeHolder>;
  // the type the session says we are on, before this tab has had its say
  accountType: string;
  storedAccountType?: string;
  liveAccountTypes: Array<string>;
}

interface ResolvedAccountType {
  accountType: string;
  // remember the outcome, so a later load in this tab starts from it
  persist: boolean;
  // tell the backend, so other devices follow
  syncSession: boolean;
}

// storage is unavailable in restricted contexts, and account selection must not fail with it
const safely = <T>(read: () => T) => {
  try {
    return read();
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const readStoredAccountType = (cognitoId: string) =>
  safely(() => sessionStorage.getItem(getAccountTypeKey(cognitoId))) ?? undefined;

export const storeAccountType = (cognitoId: string, accountType: string) =>
  safely(() => sessionStorage.setItem(getAccountTypeKey(cognitoId), accountType));

export const clearStoredAccountType = (cognitoId?: string) =>
  cognitoId ? safely(() => sessionStorage.removeItem(getAccountTypeKey(cognitoId))) : undefined;

/**
 * What this tab chose outranks the persisted session, so a switch survives a reload even when the
 * session write fails. With nothing chosen yet — a new tab, a new login — a live account is the
 * default, and demo only stands in when there is no live account.
 */
export function resolveAccountType({
  accounts,
  accountType,
  storedAccountType,
  liveAccountTypes,
}: ResolveAccountTypeArgs): ResolvedAccountType {
  const stored = accounts.some(account => account.accountType === storedAccountType) ? storedAccountType : undefined;
  if (stored != null) return { accountType: stored, persist: false, syncSession: false };

  const liveAccount = accounts.find(account => liveAccountTypes.includes(account.accountType));
  if (liveAccount == null) return { accountType, persist: accountType !== '', syncSession: false };

  return { accountType: liveAccount.accountType, persist: true, syncSession: liveAccount.accountType !== accountType };
}
